import { useCallback, useEffect, useMemo, useState } from 'react';
import ApiService from '../services/api.service';

const PAGE_SIZE = 50;
const DEBOUNCE_MS = 300;

// Server-side search; filterOptions is identity so MUI cannot re-filter away TRN matches.
const useOrganizationSearch = ({ typingCenterId, type, enabled = true, mapOption }) => {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [term, setTerm] = useState('');
  const [debouncedTerm, setDebouncedTerm] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setDebouncedTerm(term.trim()), DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [term]);

  useEffect(() => {
    let cancelled = false;

    if (!typingCenterId || !enabled) {
      setOptions([]);
      return undefined;
    }

    const fetchOptions = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await ApiService.getInternalOrganizationsPage({
          typingCenterId,
          ...(type ? { type } : {}),
          ...(debouncedTerm ? { search: debouncedTerm } : {}),
          page: 0,
          size: PAGE_SIZE
        });
        if (cancelled) return;
        const content = response?.data?.data?.content;
        const rows = Array.isArray(content) ? content : [];
        setOptions(mapOption ? rows.map(mapOption) : rows);
      } catch (err) {
        if (cancelled) return;
        setOptions([]);
        setError(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchOptions();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typingCenterId, type, enabled, debouncedTerm]);

  const onInputChange = useCallback((event, value, reason) => {
    if (reason === 'input' || reason === 'clear') setTerm(value || '');
  }, []);

  const filterOptions = useCallback((opts) => opts, []);

  return useMemo(
    () => ({ options, loading, error, onInputChange, filterOptions, searchTerm: debouncedTerm }),
    [options, loading, error, onInputChange, filterOptions, debouncedTerm]
  );
};

export default useOrganizationSearch;
